import classNames from "classnames";
import { useGrowl } from "PFApp/use_growl";
import { Button } from "PFComponents/button";
import { FileUpload } from "PFComponents/file_upload";
import { Icon } from "PFComponents/icon";
import { LoadingDots } from "PFComponents/loading_dots";
import { Typography } from "PFComponents/typography";
import { useDownloadTextFile } from "PFCore/helpers/use_download_text_file";
import { useResponseErrors } from "PFCore/helpers/use_response_errors";
import { useCustomValuesImportsInvalidate } from "PFCore/hooks/queries/admin/custom_values_imports/use_custom_values_imports_invalidate";
import { ApiRoute } from "PFCore/utilities/routes";
import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useSkillsCustomTypeId } from "../../../../../utils/use_skills_custom_type";
import { ImportModalPage } from "../../constants/import_modal_page";
import { skillsCsvTemplate } from "../../constants/skills_csv_template";
import { useImportSkillsModalContext } from "../../contexts/import_skills_modal_context";
import commonCss from "../../import_skills_modal.module.scss";
import css from "./upload_page.module.scss";

export const UploadPage = (): JSX.Element => {
  const { t } = useTranslation("skillsManagement", { keyPrefix: "header.modals.import.upload" });
  const { t: commonT } = useTranslation();
  const { setCurrentPage } = useImportSkillsModalContext();
  const { invalidate } = useCustomValuesImportsInvalidate();
  const download = useDownloadTextFile();
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>();
  const growl = useGrowl();
  const { getFirstError } = useResponseErrors();
  const skillsCustomTypeId = useSkillsCustomTypeId();

  return (
    <FileUpload
      inputRef={inputRef}
      url={ApiRoute(`/api/admin/custom_values/imports`)}
      name="csv"
      data={{
        custom_type_id: skillsCustomTypeId // eslint-disable-line camelcase
      }}
      handleChange={() => {
        setIsLoading(true);
      }}
      handleLoad={() => {
        setCurrentPage(ImportModalPage.ImportSuccess);
        invalidate();
      }}
      handleError={(response) => {
        setCurrentPage(ImportModalPage.ImportError);
        growl({
          message: getFirstError(response),
          kind: "error"
        });
      }}
      handleNetworkError={() => {
        growl({
          message: commonT("somethingWentWrong"),
          kind: "error"
        });
      }}
      handleTypeError={() => {
        growl({
          message: t("invalidFileType"),
          kind: "error"
        });
      }}
      showDraggingHint={false}
      disabled={isLoading}
      allowedFileTypes={/\.csv$/i}
      acceptMediaTypes={[".csv"]}
      classes={{
        root: css.fullSize,
        label: css.fullSize,
        content: classNames(commonCss.uploadFlowContainer, css.content)
      }}
    >
      <Icon name="import" size="xl" color="palettePrimary2" className={css.uploadIcon} />
      <Typography variant="h4" noMargin>
        {t("uploadNewCSVFile")}
      </Typography>
      <Button className={css.uploadButton} onClick={() => inputRef.current?.click()}>
        {isLoading ? <LoadingDots /> : t("upload")}
      </Button>
      <Button
        kind="text"
        onClick={() => {
          download("template.csv", skillsCsvTemplate);
        }}
      >
        <Typography variant="labelBold" className={css.downloadLink}>
          {t("downloadCSVTemplate")}
        </Typography>
      </Button>
    </FileUpload>
  );
};
